import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  useTheme,
  alpha,
  Alert,
  AlertTitle,
  CircularProgress,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import {
  CONTACT_TOPICS,
  MESSAGE_MAX,
  NAME_MAX,
  EMAIL_MAX,
  buildContactPayload,
  serverFieldErrors,
  sourcePathFromReferrer,
  topicFromQuery,
  validateContactForm,
} from '../utils/contactForm';
import { sendContactMessage } from '../services/contact';
import { trackContactSubmit } from '../analytics/conversions';

const HeroSection = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(to bottom right, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.background.default, 0.2)})`
    : `linear-gradient(to bottom right, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.background.paper, 0.2)})`,
  padding: theme.spacing(6, 0, 5),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0, 6),
  },
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%)`,
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.12)}`,
  }
}));

const SocialButton = styled(Button)(({ theme }) => ({
  minWidth: 48,
  width: 48,
  height: 48,
  borderRadius: '50%',
  padding: 0,
  '&:hover': {
    transform: 'translateY(-4px)',
  }
}));

// Bal küpü alanı: ekran dışında, ekran okuyucudan gizli, sekmeyle ulaşılamaz (botlar doldurursa sunucu sessizce yok sayar)
const honeypotSx = { position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' };

const emptyForm = (topic) => ({ name: '', email: '', topic, message: '', website: '' });

const FIELD_ORDER = ['name', 'email', 'topic', 'message'];

const Contact = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryTopic = topicFromQuery(searchParams);

  const [formData, setFormData] = useState(() => emptyForm(queryTopic));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | success
  const [submitError, setSubmitError] = useState(null);
  // Formun açıldığı an (istemci saati); gönderimde submittedAt ile birlikte gider → doldurma süresi saat farkından etkilenmez
  const formOpenedAt = useRef(Date.now());
  const mounted = useRef(true);
  const fieldRefs = useRef({});
  const successRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // ?konu= değişirse (ör. sayfadayken başka bir bağlantıdan gelinirse) konu güncellenir
  useEffect(() => {
    setFormData((f) => (f.topic === queryTopic ? f : { ...f, topic: queryTopic }));
  }, [queryTopic]);

  useEffect(() => {
    if (status === 'success') successRef.current?.focus();
  }, [status]);

  const sending = status === 'sending';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const focusFirstError = (errs) => {
    const first = FIELD_ORDER.find((f) => errs[f]);
    if (first) fieldRefs.current[first]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const invalid = validateContactForm(formData);
    setErrors(invalid);
    if (Object.keys(invalid).length > 0) {
      setSubmitError(null);
      focusFirstError(invalid);
      return;
    }

    setStatus('sending');
    setSubmitError(null);
    const payload = buildContactPayload(formData, {
      formOpenedAt: formOpenedAt.current,
      submittedAt: Date.now(),
      sourcePath: sourcePathFromReferrer(document.referrer, window.location.origin, window.location.pathname),
    });
    const res = await sendContactMessage(payload);
    if (!mounted.current) return;

    if (res.success) {
      trackContactSubmit(payload.topic);
      setStatus('success');
      return;
    }

    // Hata: yazılanlar korunur, kullanıcı düzeltip tekrar gönderebilir
    setStatus('idle');
    if (res.statusCode === 400) {
      const fieldErrors = serverFieldErrors(res.errors);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        focusFirstError(fieldErrors);
        setSubmitError(t('contact.form.errorFields'));
      } else {
        setSubmitError(res.message || t('contact.form.errorFields'));
      }
    } else if (res.statusCode === 429 || res.statusCode === 503) {
      setSubmitError(res.message || t('contact.form.error'));
    } else if (res.statusCode === 0) {
      setSubmitError(t('contact.form.errorNetwork'));
    } else {
      setSubmitError(t('contact.form.error'));
    }
  };

  const startNewMessage = () => {
    setFormData(emptyForm(queryTopic));
    setErrors({});
    setSubmitError(null);
    formOpenedAt.current = Date.now();
    setStatus('idle');
  };

  const errorText = (field) => (errors[field] ? t(`contact.form.errors.${errors[field]}`) : '');
  const messageLength = formData.message.length;

  const infoItems = [
    { icon: <LocationOnIcon />, ...t('contact.info.address', { returnObjects: true }) },
    { icon: <EmailIcon />, ...t('contact.info.email', { returnObjects: true }) },
    { icon: <AccessTimeIcon />, ...t('contact.info.workHours', { returnObjects: true }) }
  ];

  const socialLinks = [
    { icon: <FacebookIcon />, url: 'https://www.facebook.com/people/Odazen/61575716013184/', color: '#1877F2' },
    { icon: <XIcon />, url: 'https://x.com/odazenn', color: '#000000' },
    { icon: <InstagramIcon />, url: 'https://www.instagram.com/odazentr/', color: '#E4405F' },
    { icon: <LinkedInIcon />, url: 'https://www.linkedin.com/company/odazen/', color: '#0A66C2' }
  ];

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 2,
              position: 'relative',
              zIndex: 1
            }}
          >
            {t('contact.hero.title')}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              position: 'relative',
              zIndex: 1
            }}
          >
            {t('contact.hero.subtitle')}
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 4 }}>
              {t('contact.form.title')}
            </Typography>
            {status === 'success' ? (
              <Box
                ref={successRef}
                tabIndex={-1}
                role="status"
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: '16px',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                  bgcolor: alpha(theme.palette.success.main, 0.06),
                  textAlign: 'center',
                  outline: 'none',
                }}
              >
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
                <Typography component="h4" variant="h5" sx={{ fontWeight: 700, mt: 1.5 }}>
                  {t('contact.form.successTitle')}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 460, mx: 'auto', lineHeight: 1.7 }}>
                  {t('contact.form.success')}
                </Typography>
                <Button variant="outlined" onClick={startNewMessage}>
                  {t('contact.form.newMessage')}
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit} noValidate aria-busy={sending} style={{ position: 'relative' }}>
                <Box aria-hidden="true" sx={honeypotSx}>
                  <label htmlFor="contact-website">{t('contact.form.honeypot')}</label>
                  <input
                    id="contact-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label={t('contact.form.name.label')}
                      name="name"
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.form.name.placeholder')}
                      error={!!errors.name}
                      helperText={errorText('name')}
                      inputRef={(el) => { fieldRefs.current.name = el; }}
                      inputProps={{ maxLength: NAME_MAX, readOnly: sending }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label={t('contact.form.email.label')}
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.form.email.placeholder')}
                      error={!!errors.email}
                      helperText={errorText('email')}
                      inputRef={(el) => { fieldRefs.current.email = el; }}
                      inputProps={{ maxLength: EMAIL_MAX, readOnly: sending, inputMode: 'email' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      select
                      label={t('contact.form.subject.label')}
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      disabled={sending}
                      error={!!errors.topic}
                      helperText={errorText('topic')}
                      inputRef={(el) => { fieldRefs.current.topic = el; }}
                    >
                      {CONTACT_TOPICS.map((code) => (
                        <MenuItem key={code} value={code}>
                          {t(`contact.form.subject.options.${code}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={5}
                      label={t('contact.form.message.label')}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.form.message.placeholder')}
                      error={!!errors.message}
                      inputRef={(el) => { fieldRefs.current.message = el; }}
                      inputProps={{ maxLength: MESSAGE_MAX, readOnly: sending, 'aria-describedby': 'contact-message-counter' }}
                      FormHelperTextProps={{ component: 'div' }}
                      helperText={
                        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <span>{errorText('message')}</span>
                          <Box
                            component="span"
                            id="contact-message-counter"
                            sx={{
                              flexShrink: 0,
                              fontVariantNumeric: 'tabular-nums',
                              color: messageLength > MESSAGE_MAX * 0.95 ? 'warning.main' : 'text.secondary',
                            }}
                          >
                            {t('contact.form.message.counter', { count: messageLength, max: MESSAGE_MAX })}
                          </Box>
                        </Box>
                      }
                    />
                  </Grid>
                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error" role="alert">
                        <AlertTitle>{t('contact.form.errorTitle')}</AlertTitle>
                        {submitError}
                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                          {t('contact.form.textKept')}
                        </Box>
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={sending}
                      endIcon={sending ? <CircularProgress size={18} color="inherit" aria-hidden="true" /> : <SendIcon />}
                      sx={{ px: 4, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {sending ? t('contact.form.sending') : t('contact.form.submit')}
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
                      {t('contact.form.kvkk.text')}{' '}
                      <Link component={RouterLink} to="/kvkk" underline="always">
                        {t('contact.form.kvkk.link')}
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
              </form>
            )}
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={5}>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, fontWeight: 700, mb: 4 }}>
              {t('contact.info.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t('contact.info.description')}
            </Typography>
            <Grid container spacing={3}>
              {infoItems.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <InfoCard>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ color: theme.palette.primary.main }}>
                        {item.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          {item.content}
                        </Typography>
                      </Box>
                    </CardContent>
                  </InfoCard>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('contact.social.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('contact.social.description')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {socialLinks.map((social, index) => (
                  <SocialButton
                    key={index}
                    variant="contained"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: social.color,
                      '&:hover': {
                        bgcolor: alpha(social.color, 0.9)
                      }
                    }}
                  >
                    {social.icon}
                  </SocialButton>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
